import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, Calendar, MapPin, Star, Shield, 
  ChevronRight, Stethoscope, Heart,
  CheckCircle2, Building2, CheckSquare,
  Users, Phone, Loader2, Sparkles, MessageSquareText, Brain, BarChart3, ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { SearchableCombobox } from '../components/SearchableCombobox';
import SEOHead from '../components/SEOHead';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useGeoLocation from '../hooks/useGeoLocation';
import { suggestSpecialties } from '../services/searchApi';
import { SEO_IMAGES, createFaqSchema, createOrganizationSchema, createWebSiteSchema } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeSpecialtyText = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const HOME_FAQS = [
  {
    question: 'Como encontrar um médico no BuscaDoutor?',
    answer:
      'Você pode buscar por especialidade, sintoma, cidade, estado e convênio. A plataforma organiza médicos, dentistas e clínicas para facilitar a comparação antes do atendimento.',
  },
  {
    question: 'O BuscaDoutor substitui uma consulta médica?',
    answer:
      'Não. O BuscaDoutor ajuda na busca e na organização de informações, mas não substitui avaliação médica, diagnóstico ou atendimento de urgência.',
  },
  {
    question: 'Posso buscar por convênio médico?',
    answer:
      'Sim. A busca permite filtrar operadora e plano quando essa informação está disponível, aproximando o paciente de profissionais compatíveis com sua cobertura.',
  },
  {
    question: 'Profissionais e clínicas podem atualizar seus dados?',
    answer:
      'Sim. Profissionais e clínicas podem se cadastrar ou reivindicar um perfil existente para manter dados de atendimento, localização e convênios mais completos.',
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [symptom, setSymptom] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [allSpecialties, setAllSpecialties] = useState([]);
  const [availableToday, setAvailableToday] = useState([]);
  const [healthPlans, setHealthPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSearchTab, setActiveSearchTab] = useState('specialties');
  const [symptomFocused, setSymptomFocused] = useState(false);

  // AI symptom analysis state
  const [symptomText, setSymptomText] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [aiSource, setAiSource] = useState('');
  const [aiSafetyNote, setAiSafetyNote] = useState('');
  const [aiFollowUpQuestions, setAiFollowUpQuestions] = useState([]);
  const [aiRedFlags, setAiRedFlags] = useState([]);
  const [aiNextSteps, setAiNextSteps] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const symptomTimerRef = useRef(null);
  const locationRef = useRef(null);
  const symptomInputRef = useRef(null);
  const specialtyBlockRef = useRef(null);
  const insuranceBlockRef = useRef(null);

  // Geo-location auto-fill (IP-based, non-blocking). When a detection arrives
  // before the user picked anything, we pre-populate state + city. The city
  // pre-fill is deferred until the cities list for that state finishes loading.
  const { detected, persistManual } = useGeoLocation();
  const pendingAutoCityRef = useRef(null);
  const geoAutoStateAppliedRef = useRef(false);

  // Cascading filter state
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorPlans, setOperatorPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const requiredFieldClass = '!border-amber-400 !bg-amber-50/50 hover:!border-amber-500 focus:!border-amber-500 focus:!ring-2 focus:!ring-amber-300/50';
  
  const specialtiesScrollRef = useRef(null);
  const doctorsScrollRef = useRef(null);

  // Hero image carousels - Desktop & Mobile separate
  const desktopImages = [
    { src: SEO_IMAGES.home, alt: "BuscaDoutor para encontrar médicos, clínicas e especialidades" },
    { src: "https://images.pexels.com/photos/12599544/pexels-photo-12599544.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Consulta médica em clínica moderna" },
    { src: "https://images.unsplash.com/photo-1758691463000-08b98131daf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBvZmZpY2UlMjBtb2Rlcm4lMjB0ZWNobm9sb2d5JTIwdGFibGV0JTIwZGlnaXRhbCUyMGhlYWx0aHxlbnwwfHx8fDE3NzUyNDgzODJ8MA&ixlib=rb-4.1.0&q=85&w=900", alt: "Médico com tecnologia digital em consultório" },
    { src: "https://images.pexels.com/photos/3985301/pexels-photo-3985301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Profissional de saúde conversando com paciente" },
    { src: "https://images.unsplash.com/photo-1758691462774-f01ed567f2c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBvZmZpY2UlMjBtb2Rlcm4lMjB0ZWNobm9sb2d5JTIwdGFibGV0JTIwZGlnaXRhbCUyMGhlYWx0aHxlbnwwfHx8fDE3NzUyNDgzODJ8MA&ixlib=rb-4.1.0&q=85&w=900", alt: "Médico analisando exame de imagem em tablet" },
  ];
  const mobileImages = [
    { src: SEO_IMAGES.home, alt: "BuscaDoutor para encontrar atendimento de saúde" },
    { src: "https://images.unsplash.com/photo-1758691462651-611d730c5272?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx3b21hbiUyMGRvY3RvciUyMHdoaXRlJTIwY29hdCUyMGNvbmZpZGVudCUyMGFybXMlMjBjcm9zc2VkJTIwY2xpbmljfGVufDB8fHx8MTc3NTI0ODQxMHww&ixlib=rb-4.1.0&q=85&w=600", alt: "Médica em consultório moderno" },
    { src: "https://images.unsplash.com/photo-1758691463582-11aea602cd4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHx3b21hbiUyMGRvY3RvciUyMHdoaXRlJTIwY29hdCUyMGNvbmZpZGVudCUyMGFybXMlMjBjcm9zc2VkJTIwY2xpbmljfGVufDB8fHx8MTc3NTI0ODQxMHww&ixlib=rb-4.1.0&q=85&w=600", alt: "Médico sorrindo de braços cruzados" },
    { src: "https://images.unsplash.com/photo-1622253694238-3b22139576c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwc21pbGluZyUyMHN0ZXRob3Njb3BlJTIwcG9ydHJhaXQlMjBjbGVhbnxlbnwwfHx8fDE3NzUyNDgzNzd8MA&ixlib=rb-4.1.0&q=85&w=600", alt: "Enfermeiro sorrindo em hospital" },
    { src: "https://images.unsplash.com/photo-1758691461513-88a0aef72160?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHx3b21hbiUyMGRvY3RvciUyMHdoaXRlJTIwY29hdCUyMGNvbmZpZGVudCUyMGFybXMlMjBjcm9zc2VkJTIwY2xpbmljfGVufDB8fHx8MTc3NTI0ODQxMHww&ixlib=rb-4.1.0&q=85&w=600", alt: "Médico experiente em clínica" },
  ];
  const [currentImageIdx, setCurrentImageIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIdx(prev => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(timer);
  }, [selectedState, selectedCity, selectedOperator, selectedPlan]);

  useEffect(() => {
    fetchData();
  }, [selectedState, selectedCity, selectedOperator, selectedPlan]);

  // Fetch plans when operator changes
  useEffect(() => {
    if (selectedOperator) {
      setLoadingPlans(true);
      setSelectedPlan('');
      axios.get(`${API_URL}/api/public/ans/operators/${selectedOperator.registro_ans}/plans`)
        .then(res => setOperatorPlans(res.data.plans || []))
        .catch(() => setOperatorPlans([]))
        .finally(() => setLoadingPlans(false));
    } else {
      setOperatorPlans([]);
      setSelectedPlan('');
    }
  }, [selectedOperator]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedState) {
      setLoadingCities(true);
      setSelectedCity('');
      const ctrl = new AbortController();
      let retried = false;
      const fetchCities = () => {
        axios.get(`${API_URL}/api/public/cities?state=${selectedState}`, { timeout: 15000, signal: ctrl.signal })
          .then(res => {
            const list = res.data || [];
            if (list.length === 0 && !retried) {
              retried = true;
              setTimeout(fetchCities, 1500);
              return;
            }
            setCities(list);
            setLoadingCities(false);
          })
          .catch((err) => {
            if (err?.code === 'ERR_CANCELED') return;
            if (!retried) {
              retried = true;
              setTimeout(fetchCities, 1500);
            } else {
              setCities([]);
              setLoadingCities(false);
            }
          });
      };
      fetchCities();
      return () => ctrl.abort();
    } else {
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedState]);

  // Debounced AI symptom analysis
  const analyzeSymptoms = useCallback(async (text) => {
    if (!text || text.trim().length < 5) {
      setAiSuggestions([]);
      setAiSummary('');
      setAiSource('');
      setAiSafetyNote('');
      setAiFollowUpQuestions([]);
      setAiRedFlags([]);
      setAiNextSteps([]);
      setAiError('');
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const res = await suggestSpecialties({
        query: text.trim(),
        state: selectedState || '',
        city: selectedCity || '',
        operator: selectedOperator?.name || '',
        plan: selectedPlan || '',
        client: 'web',
      });
      const analysis = res.analysis || {};
      const isLlmResponse = analysis.source === 'llm';
      const llmSuggestions = isLlmResponse ? (res.suggestedSpecialties || []) : [];
      setAiSuggestions(llmSuggestions);
      setAiFollowUpQuestions([]);

      if (!isLlmResponse) {
        setAiSummary('');
        setAiSource('');
        setAiSafetyNote('');
        setAiRedFlags([]);
        setAiNextSteps([]);
        setAiError('A IA não respondeu agora. Tente novamente ou selecione a especialidade manualmente.');
        return;
      }

      setAiSummary(res.nextStep?.message || '');
      setAiSource(analysis.source || '');
      setAiSafetyNote(res.safetyAlert?.message || '');
      setAiRedFlags(res.safetyAlert?.isUrgency ? [res.safetyAlert.message].filter(Boolean) : []);
      setAiNextSteps(res.nextStep?.message ? [res.nextStep.message] : []);
      if (llmSuggestions.length === 0) {
        setAiError('A IA não encontrou uma especialidade cadastrada para essa descrição. Tente descrever com mais detalhes.');
      } else {
        setAiError('');
      }
    } catch {
      setAiSuggestions([]);
      setAiFollowUpQuestions([]);
      setAiRedFlags([]);
      setAiNextSteps([]);
      setAiError('Não foi possível analisar. Selecione a especialidade manualmente.');
    } finally {
      setAiLoading(false);
    }
  }, [selectedState, selectedCity, selectedOperator, selectedPlan]);

  const handleSymptomChange = (text) => {
    setSymptomText(text);
    setSymptom(text);
    if (symptomTimerRef.current) clearTimeout(symptomTimerRef.current);
    if (text.trim().length >= 5) {
      symptomTimerRef.current = setTimeout(() => analyzeSymptoms(text), 1500);
    } else {
      setAiSuggestions([]);
      setAiSummary('');
      setAiSource('');
      setAiSafetyNote('');
      setAiFollowUpQuestions([]);
      setAiRedFlags([]);
      setAiNextSteps([]);
      setAiError('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const suggestionName = suggestion?.name || suggestion?.nome || '';
    const suggestionKey = normalizeSpecialtyText(suggestionName);
    const suggestionSlug = normalizeSpecialtyText(suggestion?.slug || suggestionName);
    const spec = allSpecialties.find((s) => {
      const itemName = normalizeSpecialtyText(s.name);
      const itemSlug = normalizeSpecialtyText(s.slug || s.name);
      return (
        s.id === suggestion.id ||
        itemName === suggestionKey ||
        itemSlug === suggestionSlug
      );
    });

    setSelectedSpecialty(spec || {
      id: suggestion.id || `suggestion-${suggestionKey}`,
      name: suggestionName,
    });

    // Scroll to location section after the specialty field is filled.
    setTimeout(() => {
      locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  // Apply detected state once states list and detection are both available.
  // We never overwrite a state the user has already chosen.
  useEffect(() => {
    if (geoAutoStateAppliedRef.current) return;
    if (!detected?.state) return;
    if (selectedState) return;            // user already picked something
    if (!states || states.length === 0) return;
    const match = states.find((s) => (s.uf || '').toUpperCase() === detected.state.toUpperCase());
    if (!match) return;
    geoAutoStateAppliedRef.current = true;
    pendingAutoCityRef.current = detected.city || null;
    setSelectedState(match.uf);
  }, [detected, states, selectedState]);

  // After cities load for the auto-selected state, try to apply detected city.
  useEffect(() => {
    if (!pendingAutoCityRef.current) return;
    if (loadingCities) return;
    if (!cities || cities.length === 0) return;
    const wanted = pendingAutoCityRef.current;
    pendingAutoCityRef.current = null;
    const match = cities.find((c) => (c.city || '').toLowerCase() === wanted.toLowerCase());
    if (match) setSelectedCity(match.city);
  }, [cities, loadingCities]);

  const fetchData = async (isRetry = false) => {
    // axios with timeout to avoid hanging forever on network glitches
    const ax = axios.create({ timeout: 15000 });
    try {
      const [specialtiesRes, statesRes, operatorsRes] = await Promise.all([
        ax.get(`${API_URL}/api/public/specialties`).catch(() => ({ data: [], _failed: true })),
        ax.get(`${API_URL}/api/public/states`).catch(() => ({ data: [], _failed: true })),
        ax.get(`${API_URL}/api/public/ans/operators`).catch(() => ({ data: [] })),
      ]);

      setAllSpecialties(specialtiesRes.data || []);
      setSpecialties((specialtiesRes.data || []).slice(0, 12));
      setStates(statesRes.data || []);
      setOperators(operatorsRes.data || []);

      // Auto-retry once if either critical endpoint failed
      const criticalFailed = specialtiesRes._failed || statesRes._failed || (statesRes.data || []).length === 0 || (specialtiesRes.data || []).length === 0;
      if (criticalFailed && !isRetry) {
        setTimeout(() => fetchData(true), 2000);
        return;
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }

    // Non-critical data loaded separately
    axios.get(`${API_URL}/api/public/available-today?limit=10`, { timeout: 15000 })
      .then(res => setAvailableToday(res.data.doctors || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/public/health-plans`, { timeout: 15000 })
      .then(res => setHealthPlans((res.data || []).slice(0, 8)))
      .catch(() => {});
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const hasSymptom = symptom.trim().length >= 3;
    if (!selectedSpecialty && !hasSymptom) {
      alert('Descreva uma queixa ou selecione uma especialidade.');
      return;
    }
    if (selectedSpecialty && (!selectedState || !selectedCity)) {
      alert('Selecione o estado e a cidade para buscar.');
      return;
    }
    const params = new URLSearchParams();
    if (selectedSpecialty?.id && !String(selectedSpecialty.id).startsWith('suggestion-')) {
      params.set('specialty_id', selectedSpecialty.id);
    }
    if (selectedSpecialty?.name) params.set('specialty_name', selectedSpecialty.name);
    if (symptom.trim()) params.set('symptom', symptom.trim());
    if (selectedCity) params.set('city', selectedCity);
    if (selectedState) params.set('state', selectedState);
    if (selectedOperator) params.set('operator', selectedOperator.name);
    if (selectedPlan) params.set('plan', selectedPlan);

    navigate(`/buscar?${params.toString()}`);
  };

  const handleSpecialtyClick = (specialtyId) => {
    navigate(`/buscar?specialty_id=${specialtyId}`);
  };

  const searchTabs = [
    { id: 'specialties', label: 'Especialidades', ref: specialtyBlockRef },
    { id: 'location', label: 'Cidade e UF', ref: locationRef },
    { id: 'insurance', label: 'Convênios', ref: insuranceBlockRef },
    { id: 'symptoms', label: 'Orientação por sintomas', ref: symptomInputRef },
  ];

  const handleSearchTabClick = (tab) => {
    setActiveSearchTab(tab.id);
    if (tab.id === 'insurance') {
      insuranceBlockRef.current?.setAttribute('open', '');
    }
    window.setTimeout(() => {
      if (tab.id === 'symptoms') {
        symptomInputRef.current?.focus();
        return;
      }
      tab.ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <SEOHead
        title="BuscaDoutor | Encontre médicos, clínicas e especialidades"
        description="Encontre médicos, clínicas, especialidades e informações de atendimento com mais clareza. Busque por especialidade, cidade, convênio ou sintoma."
        canonicalUrl="/"
        imageUrl={SEO_IMAGES.home}
        imageAlt="BuscaDoutor - busca de médicos, clínicas, especialidades e convênios"
        structuredData={[
          createOrganizationSchema(),
          createWebSiteSchema(),
          createFaqSchema(HOME_FAQS),
        ]}
      />
      <Navbar />

      {/* Hero Section - calm, clear, trust-driven */}
      <section className="relative overflow-hidden bg-[#F5FAF8] border-b border-border/40" data-testid="hero-section">
        <div className="container-app py-10 md:py-16 lg:py-18">
          <div className="grid lg:grid-cols-[minmax(0,1.08fr)_minmax(340px,0.92fr)] gap-8 lg:gap-12 items-start">
            {/* Left Content */}
            <div className="space-y-5 md:space-y-8">
              {/* Main Headline - Light weight, large, calming */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-light text-[#1A2421] leading-[1.1] tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>
                Encontre médico, dentista ou clínica com{' '}
                <span className="font-medium text-primary">menos esforço.</span>
              </h1>

              <p className="text-base md:text-lg text-[#59706A] leading-relaxed max-w-lg">
                Busque por sintoma, especialidade, cidade ou convênio. O BuscaDoutor organiza as opções para você decidir com mais clareza.
              </p>

              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Modos de busca">
                {searchTabs.map((tab) => {
                  const isActive = activeSearchTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleSearchTabClick(tab)}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
                        isActive
                          ? 'border-primary bg-primary text-white shadow-[0_6px_18px_rgba(28,78,66,0.18)]'
                          : 'border-primary/15 bg-white/80 text-[#315A50] hover:border-primary/35 hover:bg-white'
                      }`}
                      role="tab"
                      aria-selected={isActive}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Mobile Hero Image + Trust Badges */}
              <div className="lg:hidden">
                <div className="relative rounded-[24px] overflow-hidden h-44 sm:h-52 bg-[#E8F0EE]">
                  {mobileImages.map((img, idx) => (
                    <img
                      key={img.alt}
                      src={img.src}
                      alt={img.alt}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
                      style={{ opacity: idx === currentImageIdx ? 1 : 0 }}
                      loading={idx === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={idx === 0 ? "high" : "low"}
                      width="1200"
                      height="630"
                    />
                  ))}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C4E42]/78 via-[#1C4E42]/22 to-[#F5FAF8]/10 mix-blend-multiply" />
                  {/* Carousel dots */}
                  <div className="absolute top-3 right-3 flex gap-1.5">
                    {mobileImages.map((img, idx) => (
                      <button
                        key={img.alt}
                        onClick={() => setCurrentImageIdx(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIdx ? 'bg-white w-5' : 'bg-white/50'}`}
                        aria-label={`Imagem ${idx + 1}`}
                      />
                    ))}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center justify-around text-white text-center">
                      <div>
                        <p className="text-lg font-bold" style={{fontFamily:'Outfit,sans-serif'}}>200mil+</p>
                        <p className="text-[10px] text-white/85">Profissionais</p>
                      </div>
                      <div className="w-px h-8 bg-white/30" />
                      <div>
                        <p className="text-lg font-bold" style={{fontFamily:'Outfit,sans-serif'}}>90+</p>
                        <p className="text-[10px] text-white/85">Especialidades</p>
                      </div>
                      <div className="w-px h-8 bg-white/30" />
                      <div>
                        <p className="text-lg font-bold" style={{fontFamily:'Outfit,sans-serif'}}>5.000+</p>
                        <p className="text-[10px] text-white/85">Cidades</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Form - Progressive Disclosure in grouped blocks */}
              <form onSubmit={handleSearch} className="space-y-3 rounded-[24px] border border-white/80 bg-white p-3.5 md:p-4 shadow-[0_12px_34px_rgba(28,78,66,0.07)]" data-testid="search-form" id="busca">
                <div className="flex items-start justify-between gap-4 pb-1">
                  <div>
                    <p className="text-sm font-semibold text-[#1A2421]">Comece pela sua necessidade</p>
                    <p className="text-xs text-[#59706A]">Descreva o que procura ou escolha uma especialidade diretamente.</p>
                  </div>
                  <Badge className="hidden sm:inline-flex bg-primary/10 text-primary border-0">Gratuito</Badge>
                </div>
                
                {/* Block 1: Descreva seus sintomas (AI-powered) */}
                <div className="search-block">
                  <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MessageSquareText className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421] sm:-ml-0">O que você sente ou procura?</span>
                    <span className="flex items-start gap-1.5 text-[10px] leading-snug text-[#4B625C] sm:ml-auto sm:items-center">
                      <Sparkles className="mt-0.5 w-3 h-3 flex-shrink-0 sm:mt-0" />
                      <span>A IA sugere especialidades. Em urgências, procure atendimento imediato.</span>
                    </span>
                  </div>
                  <div className="relative">
                    <textarea
                      ref={symptomInputRef}
                      placeholder="Ex: dor no joelho, manchas na pele, ansiedade, check-up cardiológico..."
                      value={symptomText}
                      onChange={(e) => handleSymptomChange(e.target.value)}
                      onFocus={() => {
                        setSymptomFocused(true);
                        setActiveSearchTab('symptoms');
                      }}
                      onBlur={() => setSymptomFocused(false)}
                      rows={symptomFocused || symptomText ? 3 : 1}
                      className="min-h-[52px] w-full rounded-xl border border-amber-400 bg-amber-50/50 px-4 py-3 text-sm leading-5 shadow-none transition-all resize-none placeholder:text-[#4B625C] outline-none hover:border-amber-500 focus:shadow-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-300/50 sm:min-h-0 sm:py-2.5"
                      data-testid="symptom-textarea"
                    />
                    {aiLoading && (
                      <div className="absolute right-3 top-3">
                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* AI Suggestions */}
                  {(aiSummary || aiSuggestions.length > 0 || aiSafetyNote || aiRedFlags.length > 0 || aiNextSteps.length > 0) && (
                    <div className="mt-3 rounded-2xl border border-primary/10 bg-primary/[0.03] p-3 space-y-2.5" data-testid="ai-suggestions">
                      <div className="flex items-start gap-2 border-b border-primary/10 pb-2">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Brain className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
                            Assistente de orientação
                            {aiSource && (
                              <span className="rounded-full bg-white border border-primary/10 px-2 py-0.5 text-[10px] text-[#59706A]">
                                {aiSource === 'llm' ? 'IA explicável' : 'triagem local'}
                              </span>
                            )}
                          </p>
                          {aiSummary && <p className="text-xs text-[#59706A] mt-1 leading-relaxed">{aiSummary}</p>}
                        </div>
                      </div>

                      {aiSuggestions.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {aiSuggestions.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => handleSuggestionClick(s)}
                              className={`group inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-left transition-all duration-200 ${
                                selectedSpecialty?.id === s.id
                                  ? 'bg-white border-primary/40 ring-1 ring-primary/20 shadow-sm text-primary'
                                  : 'bg-white border-slate-200 hover:border-primary/30 hover:shadow-sm text-[#1A2421]'
                              }`}
                              data-testid={`ai-suggestion-${s.id}`}
                              title={s.reason}
                            >
                              <span className="truncate text-sm font-semibold group-hover:text-primary">{s.name}</span>
                              {s.confidence && (
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                  {s.confidence}
                                </span>
                              )}
                              {selectedSpecialty?.id === s.id && (
                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {false && aiFollowUpQuestions.length > 0 && (
                        <div className="rounded-xl bg-white border border-slate-100 px-3 py-2">
                          <p className="text-[11px] font-semibold text-[#1A2421] mb-2">Para refinar sua busca</p>
                          <div className="space-y-1.5 max-h-10 overflow-hidden">
                            {aiFollowUpQuestions.map((question) => (
                              <p key={question} className="text-[11px] text-[#59706A] leading-relaxed flex gap-2">
                                <span className="text-primary">•</span>
                                <span>{question}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiRedFlags.length > 0 && (
                        <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2">
                          <p className="text-[11px] font-semibold text-red-700 mb-1">Sinais de alerta</p>
                          <p className="text-[11px] text-red-700 leading-relaxed line-clamp-2">{aiRedFlags[0]}</p>
                        </div>
                      )}

                      {aiNextSteps.length > 0 && (
                        <div className="rounded-xl bg-white border border-slate-100 px-3 py-2">
                          <p className="text-[11px] font-semibold text-[#1A2421] mb-2">Próximos passos</p>
                          <div className="space-y-1.5 max-h-10 overflow-hidden">
                            {aiNextSteps.map((step) => (
                              <p key={step} className="text-[11px] text-[#59706A] leading-relaxed flex gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                                <span>{step}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiSafetyNote && aiRedFlags.length === 0 && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 leading-relaxed">
                          {aiSafetyNote}
                        </p>
                      )}
                    </div>
                  )}

                  {aiError && (
                    <p className="text-xs text-amber-600 mt-2">{aiError}</p>
                  )}
                </div>

                {/* Block 2: Especialidade (manual or pre-filled by AI) */}
                <div className="search-block" ref={specialtyBlockRef}>
                  <span className="sr-only">O que você procura?</span>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Stethoscope className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421]">Especialidade <span className="text-[#D97B66]">*</span></span>
                    {selectedSpecialty && (
                      <Badge className="ml-auto bg-primary/10 text-primary text-[10px] border-0">{selectedSpecialty.name}</Badge>
                    )}
                  </div>
                  <SearchableCombobox
                    open={specialtyOpen}
                    onOpenChange={setSpecialtyOpen}
                    items={allSpecialties}
                    selectedValue={selectedSpecialty}
                    onSelect={(spec) => {
                      setSelectedSpecialty(spec);
                      setActiveSearchTab('specialties');
                    }}
                    onClear={() => setSelectedSpecialty(null)}
                    placeholder={loading ? "Carregando..." : "Selecione a especialidade"}
                    searchPlaceholder="Buscar especialidade..."
                    emptyMessage={loading ? "Carregando especialidades..." : "Nenhuma especialidade encontrada."}
                    icon={Stethoscope}
                    disabled={loading}
                    displayValue={selectedSpecialty?.name}
                    getItemKey={(s) => s.id}
                    getItemLabel={(s) => s.name}
                    isSelected={(s) => selectedSpecialty?.id === s.id}
                    className={requiredFieldClass}
                    testId="specialty-select"
                  />
                </div>

                {/* Block 3: Onde quer atendimento? */}
                <div className="search-block" ref={locationRef}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421]">Onde quer atendimento?</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#59706A] mb-1.5">Estado <span className="text-[#D97B66]">*</span></label>
                      <SearchableCombobox
                        open={stateOpen}
                        onOpenChange={setStateOpen}
                        items={states}
                        selectedValue={selectedState}
                        onSelect={(s) => {
                          geoAutoStateAppliedRef.current = true; // user took over
                          pendingAutoCityRef.current = null;
                          setActiveSearchTab('location');
                          setSelectedState(s.uf);
                          persistManual({ state: s.uf, city: null });
                        }}
                        onClear={() => setSelectedState('')}
                        placeholder={loading ? "Carregando..." : (states.length === 0 ? "Erro ao carregar, recarregue a página" : "Selecione o estado")}
                        searchPlaceholder="Buscar estado..."
                        emptyMessage={loading ? "Carregando estados..." : "Nenhum estado encontrado."}
                        icon={MapPin}
                        disabled={loading || states.length === 0}
                        displayValue={selectedState ? (states.find(s => s.uf === selectedState)?.name + ` (${selectedState})`) : null}
                        getItemKey={(s) => s.uf}
                        getItemLabel={(s) => `${s.name} (${s.uf})`}
                        getItemSearchValue={(s) => `${s.name} ${s.uf}`}
                        isSelected={(s) => selectedState === s.uf}
                        className={requiredFieldClass}
                        testId="state-select"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#59706A] mb-1.5">Cidade <span className="text-[#D97B66]">*</span></label>
                      <SearchableCombobox
                        open={cityOpen}
                        onOpenChange={setCityOpen}
                        items={cities}
                        selectedValue={selectedCity}
                        onSelect={(c) => {
                          setActiveSearchTab('location');
                          setSelectedCity(c.city);
                          persistManual({ state: selectedState, city: c.city });
                        }}
                        onClear={() => setSelectedCity('')}
                        placeholder={selectedState ? "Selecione a cidade" : "Escolha o estado primeiro"}
                        searchPlaceholder="Buscar cidade..."
                        emptyMessage="Nenhuma cidade encontrada."
                        icon={Building2}
                        disabled={!selectedState || loadingCities}
                        displayValue={loadingCities ? "Carregando..." : selectedCity || null}
                        getItemKey={(c) => c.city}
                        getItemLabel={(c) => c.city}
                        isSelected={(c) => selectedCity === c.city}
                        className={requiredFieldClass}
                        testId="city-select"
                      />
                    </div>
                  </div>
                </div>

                {/* Block 4: Convênio (optional - collapsed by default on mobile) */}
                <details className="search-block group" data-testid="insurance-block" ref={insuranceBlockRef}>
                  <summary className="flex items-center gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-[#1A2421]">Convênio</span>
                    <span className="text-xs text-[#59706A]/60 ml-1">(opcional)</span>
                    <ChevronRight className="w-4 h-4 text-[#59706A]/40 ml-auto transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-[#59706A] mb-1.5">Operadora</label>
                      <SearchableCombobox
                        open={operatorOpen}
                        onOpenChange={setOperatorOpen}
                        items={operators}
                        selectedValue={selectedOperator}
                        onSelect={(op) => {
                          setSelectedOperator(op);
                          setActiveSearchTab('insurance');
                        }}
                        onClear={() => setSelectedOperator(null)}
                        placeholder="Selecione a operadora"
                        searchPlaceholder="Buscar operadora..."
                        emptyMessage="Nenhuma operadora encontrada."
                        icon={Shield}
                        displayValue={selectedOperator?.name}
                        getItemKey={(op) => op.registro_ans}
                        getItemLabel={(op) => op.name}
                        isSelected={(op) => selectedOperator?.registro_ans === op.registro_ans}
                        testId="operator-select"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#59706A] mb-1.5">Plano</label>
                      <SearchableCombobox
                        open={planOpen}
                        onOpenChange={setPlanOpen}
                        items={operatorPlans}
                        selectedValue={selectedPlan}
                        onSelect={(plan) => {
                          setSelectedPlan(plan);
                          setActiveSearchTab('insurance');
                        }}
                        onClear={() => setSelectedPlan('')}
                        placeholder={selectedOperator ? "Selecione o plano" : "Escolha a operadora primeiro"}
                        searchPlaceholder="Buscar plano..."
                        emptyMessage="Nenhum plano encontrado."
                        icon={CheckCircle2}
                        disabled={!selectedOperator || loadingPlans}
                        displayValue={loadingPlans ? "Carregando..." : selectedPlan || null}
                        getItemKey={(plan) => plan}
                        getItemLabel={(plan) => plan}
                        isSelected={(plan) => selectedPlan === plan}
                        testId="plan-select"
                      />
                    </div>
                  </div>
                </details>
                
                {/* Search Button */}
                <Button 
                  type="submit" 
                  className="w-full h-12 md:h-14 rounded-full bg-accent text-accent-foreground hover:bg-[#E89715] font-bold text-base shadow-[0_4px_20px_rgba(245,166,35,0.24)] transition-all hover:shadow-[0_8px_30px_rgba(245,166,35,0.28)] focus-visible:ring-2 focus-visible:ring-accent/60"
                  data-testid="search-button"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Encontrar atendimento
                </Button>
              </form>
            </div>

            {/* Right Content - Hero Image Carousel */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="w-full aspect-[4/5] bg-[#E8F0EE] rounded-[32px] overflow-hidden shadow-[0_8px_30px_rgba(28,78,66,0.08)]">
                  {desktopImages.map((img, idx) => (
                    <img
                      key={img.alt}
                      src={img.src}
                      alt={img.alt}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
                      style={{ opacity: idx === currentImageIdx ? 1 : 0 }}
                      loading={idx === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={idx === 0 ? "high" : "low"}
                      width="1200"
                      height="1500"
                    />
                  ))}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1C4E42]/34 via-[#1C4E42]/12 to-[#F5FAF8]/18 mix-blend-multiply" />
                  {/* Carousel dots */}
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {desktopImages.map((img, idx) => (
                      <button
                        key={img.alt}
                        onClick={() => setCurrentImageIdx(idx)}
                        className={`h-2 rounded-full transition-all duration-300 ${idx === currentImageIdx ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
                        aria-label={`Imagem ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Trust badge */}
                <div className="absolute -bottom-4 left-6 right-6 bg-white rounded-[20px] p-5 shadow-[0_8px_30px_rgba(28,78,66,0.1)] border border-border/40">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#1A2421]" style={{fontFamily:'Outfit,sans-serif'}}>200mil+</p>
                      <p className="text-xs text-[#59706A]">Profissionais</p>
                    </div>
                    <div className="border-x border-border">
                      <p className="text-2xl font-bold text-[#1A2421]" style={{fontFamily:'Outfit,sans-serif'}}>90+</p>
                      <p className="text-xs text-[#59706A]">Especialidades</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1A2421]" style={{fontFamily:'Outfit,sans-serif'}}>5.000+</p>
                      <p className="text-xs text-[#59706A]">Cidades</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works - Clean horizontal steps */}
      <section className="py-12 md:py-16 bg-white border-b border-border/40">
        <div className="container-app">
          <h2 className="text-2xl sm:text-3xl font-medium text-[#1A2421] text-center mb-10" style={{fontFamily:'Outfit,sans-serif'}}>
            Como funciona
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { n: '1', title: 'Busque', desc: 'Encontre especialistas na sua região', icon: Search },
              { n: '2', title: 'Compare', desc: 'Veja avaliações e convênios aceitos', icon: Star },
              { n: '3', title: 'Agende', desc: 'Escolha o profissional e Marque sua Consulta', icon: Calendar },
              { n: '4', title: 'Cuide-se', desc: 'Sua saúde em boas mãos', icon: Heart },
            ].map(step => (
              <div key={step.n} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/8 flex items-center justify-center mx-auto mb-3">
                  <step.icon className="w-6 h-6 text-primary" />
                </div>
                <p className="font-semibold text-[#1A2421] text-sm md:text-base mb-1" style={{fontFamily:'Outfit,sans-serif'}}>{step.title}</p>
                <p className="text-xs md:text-sm text-[#59706A]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO/GEO FAQ */}
      <section className="py-12 md:py-16 bg-[#F7FBF9] border-b border-border/40" aria-labelledby="home-faq-title">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center mb-9">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Dúvidas frequentes</span>
            <h2 id="home-faq-title" className="mt-3 text-2xl sm:text-3xl font-medium text-[#1A2421]" style={{fontFamily:'Outfit,sans-serif'}}>
              Como o BuscaDoutor ajuda pacientes, médicos e clínicas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {HOME_FAQS.map((item) => (
              <article key={item.question} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
                <h3 className="font-semibold text-[#1A2421] mb-2">{item.question}</h3>
                <p className="text-sm leading-relaxed text-[#59706A]">{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* Investor Differentiation */}
      <section className="py-12 md:py-16 bg-[#F8FBFA] border-b border-border/40" data-testid="investor-differentiation-section">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Diferencial BuscaDoutor</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-[#1A2421] mt-3 leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
              Uma plataforma que entende demanda, não apenas lista médicos
            </h2>
            <p className="text-[#59706A] mt-4 text-base md:text-lg leading-relaxed">
              O BuscaDoutor conecta a intenção do paciente, os dados de cobertura e a qualidade do perfil médico para criar uma busca mais útil, mensurável e escalável.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {[
              { icon: Brain, title: 'Triagem orientada por sintomas', desc: 'Ajuda quem não sabe qual especialista procurar e explica a recomendação.' },
              { icon: Shield, title: 'Busca por plano e convênio', desc: 'Aproxima o paciente do profissional que realmente pode atendê-lo.' },
              { icon: BarChart3, title: 'Inteligência de demanda', desc: 'Transforma buscas sem resultado em mapa de oportunidade por cidade e especialidade.' },
              { icon: ClipboardCheck, title: 'Perfis médicos mais completos', desc: 'Estimula médicos a validar CRM, RQE, endereços, convênios e informações úteis.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-[#1A2421] text-sm mb-2">{item.title}</h3>
                <p className="text-xs text-[#59706A] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section - Two paths for providers: new vs already-listed */}
      <section className="py-16 md:py-20 bg-[#1C4E42]" data-testid="provider-cta-section">
        <div className="container-app">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B66] mb-3 block">Para profissionais</span>
            <h2 className="text-3xl md:text-4xl font-light text-white leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="font-bold">Alcance Novos Pacientes.</span>
            </h2>
            <p className="text-white/60 text-base md:text-lg mt-3 max-w-xl mx-auto">
              Escolha o caminho que melhor representa você hoje.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
            {/* CARD 1 - New registration */}
            <div
              className="bg-white rounded-3xl p-7 md:p-8 flex flex-col hover:shadow-[0_20px_60px_rgba(0,0,0,0.25)] transition-shadow"
              data-testid="cta-card-new"
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <Building2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1A2421] mb-2 leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
                Ainda não está no BuscaDoutor?
              </h3>
              <p className="text-[#59706A] text-sm md:text-base mb-6 flex-1">
                Cadastre-se gratuitamente em minutos e comece a receber pacientes.
              </p>
              <Link to="/cadastro?type=provider" data-testid="cta-register-link">
                <Button size="lg" className="w-full bg-primary hover:bg-[#2D6D5D] text-white rounded-full shadow-[0_4px_15px_rgba(28,78,66,0.2)]">
                  Cadastrar
                </Button>
              </Link>
            </div>

            {/* CARD 2 - Existing scraped record claim */}
            <div
              className="bg-white rounded-3xl p-7 md:p-8 flex flex-col hover:shadow-[0_20px_60px_rgba(0,0,0,0.25)] transition-shadow"
              data-testid="cta-card-claim"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#D97B66]/10 text-[#D97B66] flex items-center justify-center mb-5">
                <CheckSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1A2421] mb-2 leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
                Seu Perfil ou Clínica já aparece aqui?
              </h3>
              <p className="text-[#59706A] text-sm md:text-base mb-6 flex-1">
                Reivindique seu perfil e gerencie seus dados em poucos minutos.
              </p>
              <Link to="/claim" data-testid="cta-claim-link">
                <Button size="lg" variant="outline" className="w-full border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full">
                  Reivindicar perfil
                </Button>
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex justify-center mt-10">
            <div className="bg-white/8 rounded-3xl px-8 py-5 backdrop-blur-sm border border-white/10">
              <div className="grid grid-cols-3 gap-12 text-white text-center">
                <div>
                  <p className="text-3xl font-light" style={{fontFamily:'Outfit,sans-serif'}}>90+</p>
                  <p className="text-white/50 text-xs mt-1">Especialidades</p>
                </div>
                <div>
                  <p className="text-3xl font-light" style={{fontFamily:'Outfit,sans-serif'}}>100+</p>
                  <p className="text-white/50 text-xs mt-1">Convênios</p>
                </div>
                <div>
                  <p className="text-3xl font-light" style={{fontFamily:'Outfit,sans-serif'}}>5.000+</p>
                  <p className="text-white/50 text-xs mt-1">Cidades</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
